import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Announcement } from '@/hooks/useAnnouncement';

type Props = {
  visible: boolean;
  announcement: Announcement;
  onClose: () => void;
};

/** Γενική ανακοίνωση προς όλους τους χρήστες — εμφανίζεται μία φορά ανά
 * announcement_id (βλ. useAnnouncement). */
export function AnnouncementModal({ visible, announcement, onClose }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  if (!announcement) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" statusBarTranslucent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>{announcement.title}</Text>
          <ScrollView style={styles.bodyScroll}>
            <Text style={styles.body}>{announcement.body}</Text>
          </ScrollView>
          <Pressable style={styles.btn} onPress={onClose} accessibilityRole="button">
            <Text style={styles.btnText}>{t('common.ok')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.6)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.xl,
      padding: spacing.xl,
      alignItems: 'stretch',
      gap: spacing.md,
      width: '100%',
      maxWidth: 360,
      maxHeight: '80%',
    },
    title: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '700',
      textAlign: 'center',
    },
    bodyScroll: { flexGrow: 0 },
    body: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    btn: {
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: spacing.md,
      alignItems: 'center',
    },
    btnText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
  });
}
